import type { CaptionAuthoredStyle } from "@desk/core";

/**
 * Shared metadata for the 4 selectable caption STYLES (the `text` op's real
 * `style` — visual only, the gameplay effect lives in the scenario's
 * `levers.captionStyles`). Previously authored inline in TextTool's now-removed
 * `caption-style-section` grid; the on-video inline switcher (CaptionOverlay)
 * and EditorScreen's cycle handler both need the same id/label pairs and cycle
 * order, so they live here instead of duplicated per-consumer.
 */
export interface CaptionStyleOption {
  id: CaptionAuthoredStyle;
  label: string;
}

/** Cycle order the on-video ‹ prev · name · next › switcher steps through. */
export const CAPTION_STYLE_OPTIONS: CaptionStyleOption[] = [
  { id: "documentary", label: "Documentary" },
  { id: "clean", label: "Clean" },
  { id: "kinetic", label: "Kinetic" },
  { id: "urgent", label: "Urgent" },
];

export const DEFAULT_CAPTION_STYLE: CaptionAuthoredStyle = "documentary";

/** Human label for a style id (falls back to the raw id if somehow unknown). */
export function captionStyleLabel(style: CaptionAuthoredStyle): string {
  return CAPTION_STYLE_OPTIONS.find((o) => o.id === style)?.label ?? style;
}

/** Step to the prev (-1) / next (+1) style in `CAPTION_STYLE_OPTIONS`, wrapping. */
export function cycleCaptionStyleId(
  current: CaptionAuthoredStyle,
  dir: -1 | 1,
): CaptionAuthoredStyle {
  const ids = CAPTION_STYLE_OPTIONS.map((o) => o.id);
  const idx = ids.indexOf(current);
  const n = ids.length;
  const nextIdx = (((idx === -1 ? 0 : idx) + dir) % n + n) % n;
  return ids[nextIdx]!;
}
